import { Link } from "react-router-dom";
import { BRAND, FOOTER_PORTALS } from "@/lib/brand";
import { Phone, Mail, Globe, MapPin, Facebook, Instagram, KeyRound, ShieldCheck } from "lucide-react";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-nkf-navy text-white pt-14 pb-6 mt-16">
      <div className="nkf-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
        {/* Brand block */}
        <div className="md:col-span-2 lg:col-span-4">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="NKF" className="h-14 w-14 sm:h-16 sm:w-16 object-contain bg-white rounded-full p-1" />
            <div className="min-w-0">
              <div className="font-display text-base sm:text-lg leading-tight">National Karate Federation</div>
              <div className="hindi text-nkf-gold text-sm sm:text-base">राष्ट्रीय कराटे महासंघ</div>
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed mt-4 sm:mt-5 max-w-md">
            The official national governing body for Traditional, Semi-Contact and Full-Contact Karate across India. Building a transparent, unified ecosystem for athletes, coaches, referees, and federations.
          </p>
          <div className="mt-5 space-y-2 text-sm text-white/80">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-nkf-gold shrink-0" />
              <div className="flex flex-wrap gap-x-2">
                {BRAND.contactPhones.map((p, i) => (
                  <span key={p} className="inline-flex items-center gap-2">
                    {i > 0 && <span className="text-white/30">·</span>}
                    <a href={`tel:${p.replace(/\s+/g, "")}`} className="hover:text-nkf-gold transition" data-testid={`footer-phone-${i}`}>{p}</a>
                  </span>
                ))}
              </div>
            </div>
            <a href={`mailto:${BRAND.contactEmail}`} className="flex items-center gap-2 hover:text-nkf-gold transition group" data-testid="footer-email">
              <Mail className="h-4 w-4 text-nkf-gold shrink-0" /> <span className="group-hover:underline">{BRAND.contactEmail}</span>
            </a>
            <a href={`https://${BRAND.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-nkf-gold transition group" data-testid="footer-website">
              <Globe className="h-4 w-4 text-nkf-gold shrink-0" /> <span className="group-hover:underline">{BRAND.website}</span>
            </a>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-nkf-gold mt-0.5 shrink-0" /> New Delhi, India</div>
          </div>
          <div className="flex items-center gap-3 mt-5">
            {[
              { Ic: Facebook,  href: "https://www.facebook.com/NationalKarateFederation/", label: "Facebook" },
              { Ic: Instagram, href: "https://www.instagram.com/nkf_national_karate_federation", label: "Instagram" },
            ].map(({ Ic, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`footer-social-${label.toLowerCase()}`}
                aria-label={label}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-nkf-gold hover:text-nkf-navy transition"
              >
                <Ic className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Federation links */}
        <div className="lg:col-span-3">
          <div className="font-display text-sm tracking-[0.25em] text-nkf-gold uppercase">Federation</div>
          <ul className="mt-3 sm:mt-4 grid grid-cols-2 md:grid-cols-1 gap-y-1.5 sm:gap-y-2 text-sm text-white/80">
            {[
              ["About NKF", "/about"],
              ["Memberships", "/memberships"],
              ["Affiliations", "/affiliations"],
              ["Coaches", "/coaches"],
              ["Championships", "/championships"],
              ["Recognitions", "/recognitions"],
              ["Examinations", "/examinations"],
              ["Downloads", "/downloads"],
              ["Media Center", "/media"],
              ["News", "/news"],
            ].map(([l, t]) => (
              <li key={t}><Link to={t} className="hover:text-nkf-gold">{l}</Link></li>
            ))}
          </ul>
        </div>

        {/* LOGIN PORTALS (the only place login lives) */}
        <div className="md:col-span-2 lg:col-span-5">
          <div className="font-display text-sm tracking-[0.25em] text-nkf-gold uppercase flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Federation Login Portals
          </div>
          <p className="text-white/60 text-xs mt-2 max-w-md">
            Secure access for all registered NKF stakeholders. Use your federation credentials.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5" data-testid="footer-login-portals">
            {FOOTER_PORTALS.map((p) => (
              <Link
                key={p.role}
                to={`/login?role=${p.role}`}
                data-testid={`login-portal-${p.role}`}
                className="group flex items-center justify-between gap-2 px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-nkf-gold hover:text-nkf-navy text-[12.5px] font-semibold transition"
              >
                <span className="truncate">{p.label.replace(" Login", "")}</span>
                <ShieldCheck className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
          <div className="mt-5 p-3 bg-white/5 border border-white/10 rounded text-xs text-white/70">
            New to NKF? <Link to="/register" className="text-nkf-gold font-semibold hover:underline">Register as Member</Link> or <Link to="/contact" className="text-nkf-gold font-semibold hover:underline">Contact the Federation</Link>.
          </div>
        </div>
      </div>

      <div className="nkf-container mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60">
        <div>© National Karate Federation – NKF Bharat. All rights reserved.</div>
        <div className="flex items-center gap-4">
          <span className="hindi text-nkf-gold">{BRAND.slogan.join(" | ")}</span>
        </div>
      </div>
    </footer>
  );
}
